import axios from 'axios';
import config from 'shared/config';
import setBaseRequest from 'shared/utils/setBaseRequest';
import { addCache } from 'redux/domain/Cache/actions';
import { asyncStart, asyncSuccess, asyncError } from 'redux/domain/AsyncStatus/actions';
import { batchActions } from 'redux-batched-actions';
import { createNotification } from 'redux/domain/Notifications/creators';
import {
	setProviderId,
	setReceivedProviderToUseFromSTORIS,
	setStoreName,
	setDefaultProvider,
	setCFATraceId,
} from 'redux/domain/Welcome/creators';
import {
	HOUSING_POSITION_UNSPEFICIED,
	HOUSING_POSITION_OWN,
	HOUSING_POSITION_RENT,
	HOUSING_POSITION_PARENTS_RELATIVE,
	HOUSING_POSITION_OTHER,
	OWN_RENT_OPTIONS,
} from 'features/ApplicationEntry/constants';
import keyPath from './keyPath';
import { setCompletedAppStatusFields } from '../AppStatus';
import isLocalStorageAvailable from 'shared/utils/isLocalStorageAvailable';

export const fetchSettings = (asyncId, STORISLocationId, cfaTraceId) => dispatch => {
	const asyncKeyPath = keyPath(asyncId);
	const cancellationSource = axios.CancelToken.source();
	// begin fetch by setting async status and storing the cancellation source
	dispatch(asyncStart(asyncKeyPath, cancellationSource));

	// fetch
	return axios
		.get(
			`${config.ESTORIS_BASE_PATH}/GetFRSettings`,
			setBaseRequest({ STORISLocationId, cfaTraceId }, cancellationSource.token),
		)
		.then(settingResponse => {
			// leave settings null if not found
			if (settingResponse.status === 404) {
				dispatch(asyncSuccess(asyncKeyPath));
				return Promise.resolve;
			}

			const settings = {
				primary: {},
				coApp: {},
				comboFields: {},
				comboFieldsByValue: {},
				generalSettings: settingResponse.data.generalSettings,
				fieldAndFooterSettings: settingResponse.data.fieldAndFooterSettings,
				appStatusSettings: settingResponse.data.appStatusSettings,
			};

			settingResponse.data.storisFRCreditAppFieldSettings.forEach(value => {
				if (value.fieldSubHeaderType === 'PRIMARYAPPLICANT') {
					settings.primary[value.fieldId] = value;
				} else if (value.fieldSubHeaderType === 'COAPPLICANT') {
					settings.coApp[value.fieldId] = value;
				}
			});

			settingResponse.data.comboFields.forEach(value => {
				if (settings.comboFields[value.fieldName] === undefined) {
					settings.comboFields[value.fieldName] = [];
				}
				if (settings.comboFieldsByValue[value.fieldName] === undefined) {
					settings.comboFieldsByValue[value.fieldName] = {};
				}
				settings.comboFields[value.fieldName].push({
					value: value.returnValue,
					displayText: value.displayText,
				});
				settings.comboFieldsByValue[value.fieldName][value.returnValue] = value;
			});

			// primary housing options
			settings.comboFields.PRIMARYHOUSING = [];
			settings.comboFields.PRIMARYHOUSING.push(OWN_RENT_OPTIONS[HOUSING_POSITION_UNSPEFICIED]);
			if (settings.fieldAndFooterSettings.ApplicationPageSettings.HousingIndicatorOwn) {
				settings.comboFields.PRIMARYHOUSING.push(OWN_RENT_OPTIONS[HOUSING_POSITION_OWN]);
			}
			if (settings.fieldAndFooterSettings.ApplicationPageSettings.HousingIndicatorRent) {
				settings.comboFields.PRIMARYHOUSING.push(OWN_RENT_OPTIONS[HOUSING_POSITION_RENT]);
			}
			if (settings.fieldAndFooterSettings.ApplicationPageSettings.HousingIndicatorParentsRelative) {
				settings.comboFields.PRIMARYHOUSING.push(
					OWN_RENT_OPTIONS[HOUSING_POSITION_PARENTS_RELATIVE],
				);
			}
			if (settings.fieldAndFooterSettings.ApplicationPageSettings.HousingIndicatorOther) {
				settings.comboFields.PRIMARYHOUSING.push(OWN_RENT_OPTIONS[HOUSING_POSITION_OTHER]);
			}

			// co-app housing options
			settings.comboFields.COAPPHOUSING = [];
			settings.comboFields.COAPPHOUSING.push(OWN_RENT_OPTIONS[HOUSING_POSITION_UNSPEFICIED]);
			if (settings.fieldAndFooterSettings.ApplicationPageSettings.CoAppHousingIndicatorOwn) {
				settings.comboFields.COAPPHOUSING.push(OWN_RENT_OPTIONS[HOUSING_POSITION_OWN]);
			}
			if (settings.fieldAndFooterSettings.ApplicationPageSettings.CoAppHousingIndicatorRent) {
				settings.comboFields.COAPPHOUSING.push(OWN_RENT_OPTIONS[HOUSING_POSITION_RENT]);
			}
			if (settings.fieldAndFooterSettings.ApplicationPageSettings.CoAppHousingIndicatorParentsRelative) {
				settings.comboFields.COAPPHOUSING.push(OWN_RENT_OPTIONS[HOUSING_POSITION_PARENTS_RELATIVE]);
			}
			if (settings.fieldAndFooterSettings.ApplicationPageSettings.CoAppHousingIndicatorOther) {
				settings.comboFields.COAPPHOUSING.push(OWN_RENT_OPTIONS[HOUSING_POSITION_OTHER]);
			}

			if (isLocalStorageAvailable && cfaTraceId === '') {
				localStorage.setItem('cfaTraceId', settingResponse.data.cfaTraceId);
			}

			// update cache first and then async status so cache is ready for use.
			return dispatch(
				batchActions([
					addCache(['settings'], settings),
					setProviderId(settingResponse.data.providerToUse),
					setReceivedProviderToUseFromSTORIS(settingResponse.data.receivedProviderToUseFromSTORIS),
					setStoreName(settingResponse.data.storeName),
					setDefaultProvider(settingResponse.data.defaultProvider),
					asyncSuccess(asyncKeyPath),
					setCompletedAppStatusFields(
						settingResponse.data.appStatusSettings.CompletedAppStatusTitle,
						settingResponse.data.appStatusSettings.CompletedAppStatusText,
					),
					setCFATraceId(settingResponse.data.cfaTraceId)
				]),
			);
		})
		.catch(error => {
			if (axios.isCancel(error)) {
				return;
			}

			// fetch completed in error and present a notification
			dispatch(
				batchActions([
					asyncError(asyncKeyPath, error),
					createNotification('Trouble retrieving settings'),
				]),
			);
		});
};
